import { useMemo, useState } from "react";

import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import {
  Box,
  IconButton,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

function copyMetadataText(value) {
  const text = String(value || "");
  if (!text) return Promise.reject(new Error("empty-copy-value"));

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }

  if (typeof document === "undefined") {
    return Promise.reject(new Error("clipboard-unavailable"));
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied ? Promise.resolve() : Promise.reject(new Error("clipboard-copy-failed"));
  } catch (error) {
    document.body.removeChild(textarea);
    return Promise.reject(error);
  }
}

function MessageMetadataRow({ copied, field, index, onCopy }) {
  const copyId = `${field.label}-${index}`;

  return (
    <Stack spacing={0.35} sx={{ minWidth: 0 }}>
      <Typography sx={{ color: "text.secondary", fontSize: 10.5, lineHeight: 1.2 }}>
        {field.label}
      </Typography>

      <Stack
        alignItems="center"
        direction="row"
        spacing={0.75}
        sx={(theme) => ({
          backgroundColor: theme.palette.surface.muted,
          borderRadius: "4px",
          minHeight: 31,
          minWidth: 0,
          px: 0.75,
          py: 0.5,
        })}
      >
        <Typography
          sx={{
            color: "text.primary",
            flex: 1,
            fontSize: 11.2,
            lineHeight: 1.35,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={field.value}
        >
          {field.value}
        </Typography>

        {field.copyable ? (
          <Tooltip title={copied === copyId ? "Copiado" : `Copiar ${field.label}`}>
            <IconButton
              aria-label={`Copiar ${field.label}`}
              onClick={(event) => {
                event.stopPropagation();
                onCopy(copyId, field.value);
              }}
              size="small"
              sx={(theme) => ({
                borderRadius: "4px",
                color: theme.palette.text.secondary,
                flexShrink: 0,
                height: 22,
                width: 22,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.text.primary, 0.06),
                  color: theme.palette.text.primary,
                },
              })}
            >
              <ContentCopyRoundedIcon sx={{ fontSize: 13.5 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Stack>
  );
}

function MessageMetadataStatusPill({ status }) {
  if (!status?.label) return null;

  return (
    <Box
      component="span"
      sx={(theme) => ({
        alignItems: "center",
        backgroundColor: theme.palette.mode === "dark"
          ? alpha(theme.palette.custom.blue[500], 0.16)
          : theme.palette.custom.blue[100],
        border: "1px solid",
        borderColor: theme.palette.mode === "dark"
          ? alpha(theme.palette.custom.blue[300], 0.24)
          : alpha(theme.palette.custom.blue[200], 0.8),
        borderRadius: "999px",
        color: theme.palette.mode === "dark"
          ? theme.palette.custom.blue[200]
          : theme.palette.custom.blue[700],
        display: "inline-flex",
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        minHeight: 22,
        px: 0.85,
      })}
    >
      {status.label}
    </Box>
  );
}

function MessageMetadataRows({ copied, indexOffset = 0, onCopy, rows }) {
  const blocks = [];
  let rowIndex = 0;

  while (rowIndex < rows.length) {
    const field = rows[rowIndex];

    if (field.layout === "half") {
      const group = [];

      while (rowIndex < rows.length && rows[rowIndex].layout === "half" && group.length < 2) {
        group.push({ field: rows[rowIndex], originalIndex: rowIndex });
        rowIndex += 1;
      }

      blocks.push(
        <Box
          key={`half-${group.map((item) => item.field.label).join("-")}-${rowIndex}`}
          sx={{
            display: "grid",
            gap: 0.9,
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          {group.map(({ field: groupField, originalIndex }) => (
            <MessageMetadataRow
              copied={copied}
              field={groupField}
              index={indexOffset + originalIndex}
              key={`${groupField.label}-${groupField.value}-${originalIndex}`}
              onCopy={onCopy}
            />
          ))}
        </Box>,
      );
      continue;
    }

    blocks.push(
      <MessageMetadataRow
        copied={copied}
        field={field}
        index={indexOffset + rowIndex}
        key={`${field.label}-${field.value}-${rowIndex}`}
        onCopy={onCopy}
      />,
    );
    rowIndex += 1;
  }

  return blocks;
}

export default function MessageMetadataMenu({ metadata }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState("");
  const open = Boolean(anchorEl);
  const resolvedMetadata = useMemo(
    () => ({
      dates: metadata?.dates || [],
      rows: metadata?.rows || [],
      status: metadata?.status || null,
    }),
    [metadata],
  );

  const handleCopy = (copyId, value) => {
    copyMetadataText(value)
      .then(() => {
        setCopied(copyId);
        window.setTimeout(() => {
          setCopied((current) => (current === copyId ? "" : current));
        }, 1400);
      })
      .catch(() => {
        setCopied("");
      });
  };

  return (
    <>
      <Tooltip title="Detalhes da mensagem">
        <IconButton
          aria-label="Detalhes da mensagem"
          aria-controls={open ? "contact-message-metadata-menu" : undefined}
          aria-haspopup="menu"
          disableFocusRipple
          disableRipple
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
          size="small"
          sx={(theme) => ({
            backgroundColor: "transparent",
            color: theme.palette.text.secondary,
            height: 26,
            position: "absolute",
            right: 6,
            top: 6,
            width: 26,
            zIndex: 2,
            "&:focus-visible": {
              outline: `1px solid ${alpha(theme.palette.text.primary, 0.28)}`,
              outlineOffset: 1,
            },
            "&:hover": {
              backgroundColor: "transparent",
              color: theme.palette.text.primary,
            },
          })}
        >
          <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        id="contact-message-metadata-menu"
        onClose={() => setAnchorEl(null)}
        open={open}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        MenuListProps={{ sx: { p: 0 } }}
        PaperProps={{
          sx: (theme) => ({
            border: "1px solid",
            borderColor: theme.palette.border.default,
            borderRadius: "6px",
            boxShadow: `0 6px 14px ${alpha(theme.palette.common.black, 0.12)}`,
            maxWidth: "calc(100vw - 32px)",
            mt: 0.5,
            overflow: "hidden",
            width: 282,
          }),
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "border.default",
            px: 1.5,
            py: 1.15,
          }}
        >
          <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1.25}>
            <Typography sx={{ color: "text.primary", fontSize: 12.5, fontWeight: 800 }}>
              Detalhes da mensagem
            </Typography>
            <MessageMetadataStatusPill status={resolvedMetadata.status} />
          </Stack>
        </Box>

        {resolvedMetadata.dates.length ? (
          <Box
            sx={{
              display: "grid",
              gap: 0.9,
              gridTemplateColumns: resolvedMetadata.dates.length === 1 ? "1fr" : "1fr 1fr",
              px: 1.5,
              py: 1.15,
            }}
          >
            {resolvedMetadata.dates.map((field, index) => (
              <MessageMetadataRow
                copied={copied}
                field={field}
                index={index}
                key={`${field.label}-${field.value}-${index}`}
                onCopy={handleCopy}
              />
            ))}
          </Box>
        ) : null}

        {resolvedMetadata.rows.length ? (
          <Stack
            spacing={0.9}
            sx={{
              borderTop: resolvedMetadata.dates.length ? "1px solid" : 0,
              borderColor: "border.default",
              px: 1.5,
              py: 1.15,
            }}
          >
            <MessageMetadataRows
              copied={copied}
              indexOffset={resolvedMetadata.dates.length}
              onCopy={handleCopy}
              rows={resolvedMetadata.rows}
            />
          </Stack>
        ) : null}
      </Menu>
    </>
  );
}
